import './DashGroupedBarChart.css';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartOptions
} from 'chart.js';
import { ILineChartData } from '../../../interfaces/type1/ILineChartData';
import { useRealoadOnThemeToogle } from '../../../components/hooks/useRealodOnThemeToogle';
import { useColors as useGrayColors } from '../../../components/hooks/useColors';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

export default function DashGroupedBarChart(props: { Data: ILineChartData & { Horizontal?: boolean } }) {
    const fontColor = useRealoadOnThemeToogle();
    const { getColors } = useGrayColors();

    const getDataSet = () => {
        if (props.Data.DataSets.SeriesGroupFunction) {
            const sets = props.Data.DataSets.SeriesGroupFunction();
            return sets.map((d, index) => ({
                label: d.Key,
                data: d.Data.Select(s => s.Total),
                backgroundColor:
                    d.ColorIndex !== undefined
                        ? getColors(10)[d.ColorIndex!]
                        : getColors(10)[index],
                borderRadius: 2,
                borderSkipped: false,
                datalabels: {
                    display: false,
                },
            }));
        } else {
            return [
                {
                    label: props.Data.DataSets.Title,
                    data: props.Data.DataSets.Data.Select(s => s.Total),
                    backgroundColor: getColors(props.Data.DataSets.Data.Count()),
                    borderRadius: 2,
                    borderSkipped: false,
                    datalabels: {
                        display : false
                    }
                },
            ];
        }
    };

    const data = {
        labels: props.Data.LabelFormat
            ? props.Data.DataSets.Data.Select(s => props.Data.LabelFormat!(s.Group)).Distinct()
            : props.Data.DataSets.Data.Select(s => s.Group).Distinct(),
        datasets: getDataSet().Where(s => s.data.Any(s => s > 0)),
    };

    

    const options: ChartOptions<'bar'> = {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: !!props.Data.Horizontal ? 'y' : 'x',
        plugins: {
            legend: {
                display: true,
                labels: {
                    color: fontColor,
                    font: {
                        family: 'Oswald',
                        size: 12,
                    },
                },
            },
            title: {
                display: false,
                text: props.Data.Title,
                color: fontColor,
                font: {
                    family: 'Oswald',
                    size: 19,
                },
                padding: {
                    bottom: 25,
                },
            },
            tooltip: {
                enabled: props.Data.EnableTooltip ?? true,
                callbacks: {
                    title: (tooltipItems) => {
                        const labelIndex = tooltipItems[0].dataIndex;
                        return data.labels[labelIndex];
                    },
                    label: (tooltipItem) => {
                        const index = tooltipItem.dataIndex;
                        const total = tooltipItem.dataset.data[index];

                        if (props.Data.ToolTipFormatter) {
                            if (props.Data.DataSets.SeriesGroupFunction) {
                                const sets = props.Data.DataSets.SeriesGroupFunction();
                                const item = sets.find((s) => s.Key == tooltipItem.dataset.label);

                                if (item)
                                    return props.Data.ToolTipFormatter(item.Data[tooltipItem.dataIndex]);
                            }
                            return props.Data.ToolTipFormatter(props.Data.DataSets.Data[tooltipItem.dataIndex]);
                        } else {
                            return `Total ${total}`;
                        }
                    },
                },
            },
        },
        scales: {
            x: {
                stacked: false,
                ticks: {
                    color: fontColor,
                    font: {
                        family: 'Oswald',
                    },
                },
                grid: {
                    display: false,
                },
            },
            y: {
                stacked: false,
                ticks: {
                    color: fontColor,
                    font: {
                        family: 'Oswald',
                    },
                },
                grid: {
                    display: false,
                    drawTicks: true,
                },
            },
        },
    };

    if (props.Data.XTickFormatter) {


        if (!!props.Data.Horizontal) {
            options.scales!.y!.ticks!.callback = function (v: any) {

                if(!props.Data.DataSets.Data.Any())
                    return "";

                const item = props.Data.DataSets.Data[v];
                const label = item.Group;
                if (props.Data.XTickFormatter) return props.Data.XTickFormatter(item);
                return `${label}`;
            };
        } else {
            options.scales!.x!.ticks!.callback = function (v: any) {

                if(!props.Data.DataSets.Data.Any())
                    return "";

                const item = props.Data.DataSets.Data[v];
                const label = item.Group;
                if (props.Data.XTickFormatter) return props.Data.XTickFormatter(item);
                return `${label}`;
            };
        }
    }


    if (props.Data.YTickFormatter) {
        if (!!props.Data.Horizontal) {
            options.scales!.x!.ticks!.callback = function (v: any) {

                   if(!props.Data.DataSets.Data.Any())
                    return "";


                if (props.Data.YTickFormatter)
                    return props.Data.YTickFormatter(v);
                return `${v}`;
            };
        } else {
            options.scales!.y!.ticks!.callback = function (v: any) {

                   if(!props.Data.DataSets.Data.Any())
                    return "";


                if (props.Data.YTickFormatter)
                    return props.Data.YTickFormatter(v);
                return `${v}`;
            };
        }


    }


    return (
        <div className="DashGroupedBarChartComponent">
            <Bar data={data} options={options} />
        </div>
    );
}
